import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import Table from '@risksmart-app/components/src/table';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { PageLayout } from 'src/layouts';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import { useGetChangeRequests } from '@/hooks/queries/change-request/useGetChangeRequests';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useRibbonAndExport } from '@/hooks/useRibbonAndExport';
import { getCounter } from '@/utils/collectionUtils';

import { useGetCollectionTableProps } from './config';
import { RequestsRegisterToolbar } from './RequestsRegisterToolbar';
import type { ChangeRequestRegisterFields } from './types';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';

const Page = () => {
  const location = useLocation();
  const { t } = useTranslation('common', { keyPrefix: 'requests' });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const { user } = useRisksmartUser();
  const { data, loading } = useGetChangeRequests({
    queryArgs: {},
    shouldSkip: !user?.userId,
  });
  const [selectedItems, setSelectedItems] = useState<
    ChangeRequestRegisterFields[]
  >([]);
  const tableProps = useGetCollectionTableProps(data);
  const title = t('register_title');
  const counter = getCounter(tableProps.totalItemsCount, loading);

  const { ribbonProps } = useRibbonAndExport(useGetDefaultRibbonFilters);

  useEffect(() => {
    const isFromRedirect = !!location.hash;

    if (!isFromRedirect) {
      tableProps.actions.setPropertyFiltering({
        tokens: [
          {
            propertyKey: 'RequiresAction',
            value: 'true',
            operator: '=',
          },
        ],
        operation: 'and',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <PageLayout
      helpTranslationKey={'requests.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-136) — flag-gated. Mounted only when the flag
      // is on; the Requests register has no export/create, so the toolbar
      // surfaces the relocated ribbon "Edit Filters" opener plus the
      // auto-injected Toolbar settings action.
      headerOverride={
        objectToolbarEnabled ? (
          <RequestsRegisterToolbar title={title} counter={counter} />
        ) : undefined
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={Parent_Type_Enum.ChangeRequest}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />
      <Table
        {...tableProps}
        selectionType={'multi'}
        selectedItems={selectedItems}
        onSelectionChange={(e) => setSelectedItems(e.detail.selectedItems)}
        trackBy={'Id'}
      />
    </PageLayout>
  );
};

export default Page;
